import { EventEmitter, Injectable } from '@angular/core';
import {BUID, BUIDMid} from '../models/BUID';
import { MerchantService } from './merchant.service';
import {UserProfile} from "../models/user-profile";
import { getBusinessUnitsForActiveOrg } from './organisation-context.service';
import {NotificationService} from "./notification.service";

type ChangeCallack = {
  name: string,
  url: string,
  callback: (buid: string) => void
}

@Injectable({ providedIn: 'root' })
export class MerchantChangeService {
  public merchantChangeEvent: EventEmitter<{ buid: string, mid: string }> = new EventEmitter<{ buid: string, mid: string }>();
  public addPreviewBuid: EventEmitter<BUID> = new EventEmitter<BUID>();
  /** Asks the app shell's merchant picker to open (optionally straight into full-screen). */
  public openPickerRequest: EventEmitter<{ fullscreen: boolean }> = new EventEmitter<{ fullscreen: boolean }>();
  private changeListeners: ChangeCallack[] = [];

  constructor(private merchantService: MerchantService, private notificationService: NotificationService) {
  }

  public onChange(name: string, requiredUrl: string, callback: (buid: string) => void): void {
    // check if url is already registered
    const existingListener = this.changeListeners.findIndex(c => c.name === name && c.url === requiredUrl);
    if (existingListener > -1) {this.changeListeners.splice(existingListener, 1); }
    this.changeListeners.push({ name: name, url: requiredUrl, callback: callback });
  }

  public setActiveMerchant(merchant: BUID): void {
    try {
      const mid = this.merchantService.getCurrentBuid();
      // re-set selected mid
      const currentSelectedMid = this.merchantService.getCurrentMid();
      const midInBuid = currentSelectedMid?.monekId !== undefined
        && (merchant.mids ?? []).findIndex(m => m.monekId === currentSelectedMid.monekId) !== -1
      if (!midInBuid) {
        this.setActiveMid(merchant);
        return;
      }

      // check were not setting the same merchant causing unnecessary reloads.
      // Compare by id + selected mid rather than full JSON: setActiveMid writes
      // isSelected flags onto the stored copy, so a stringify comparison against
      // the profile copy never matches and every refresh re-emits.
      const storedSelectedMid = mid?.mids?.find(m => m.isSelected)?.monekId ?? currentSelectedMid.monekId;
      if (mid !== null && merchant.id == mid.id && storedSelectedMid === currentSelectedMid.monekId) {
        return;
      }

      localStorage.setItem('current-merchant-buid', JSON.stringify(merchant));

      this.changeListeners.forEach((c: ChangeCallack) => {
        if (window.location.href === c.url) // only call callback if we are on the correct page
          c.callback(merchant.id)
      });
      this.merchantChangeEvent.emit({ buid: merchant.id, mid: currentSelectedMid.monekId });
    } catch {
      // logging
    }
  }

  public setActiveMid(merchant: BUID, acquirerMid?: BUIDMid): void {
    if (acquirerMid === undefined) {
      // set primary mid to selected mid if acquirerMid is not set
      merchant.mids = merchant.mids.map(m => {
        if (m.isPrimary && m.status.toUpperCase() === 'ACTIVE') m.isSelected = true;
        return m;
      });
    } else {
      // set selected mid to primary mid
      merchant.mids = merchant.mids.map(m => {
        m.isSelected = m.monekId === acquirerMid.monekId;
        return m;
      });
    }

    let selectedMid = merchant.mids.find(m => m.isSelected); // get the selected mid (works if previously logged in)
    if (selectedMid === undefined) {
      selectedMid = merchant.mids.find(m => m.isPrimary && m.status.toUpperCase() === 'ACTIVE'); // get the default active mid (new user login / new device)
    }
    if (selectedMid === undefined) {
      selectedMid = merchant.mids.find(m => m.status.toUpperCase() === 'ACTIVE'); // get any active mid (if no primary mid is set)
    }

    if (selectedMid == undefined) {
      const message = 'We were unable to select a mid. Please contact support to resolve this issue.';
      this.notificationService.ErrorSmallDialog(message, new Error(message));
    }

    localStorage.setItem('current-merchant-buid', JSON.stringify(merchant));
    localStorage.setItem('current-merchant-mid', JSON.stringify(selectedMid));

    this.changeListeners.forEach((c: ChangeCallack) => {
      if (window.location.href === c.url) // only call callback if we are on the correct page
        c.callback(merchant.id)
    });
    this.merchantChangeEvent.emit({ buid: merchant.id, mid: selectedMid?.monekId || '' });
  }

  public getMerchantRefs(): BUID[] {
    try {
      const profileString = localStorage.getItem('profile');
      if (profileString == null) return [];
      const profile = JSON.parse(profileString) as UserProfile;
      if (profile == null) return [];
      return getBusinessUnitsForActiveOrg(profile);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch(exception: any) {
      return [];
    }
  }
}
